import { useI18n } from "@/lib/hooks/useI18n";
import {
  IResourcePermission,
  parseDateOnlyString,
  WeekDayNames
} from "@gettactic/api";
import { useAuthenticated } from "@gettactic/hooks";
import { format } from "date-fns";
import { AnchorDaysObject } from "@/ui/controls/DatePicker/AnchorDayPicker";
import { cn } from "@gettactic/helpers/src/css/cn";

type Props = {
  permission: IResourcePermission;
};

export function PermissionsDatesCell({ permission }: Props) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { templates } = useI18n(authenticatedUser);

  const since = permission.since ? parseDateOnlyString(permission.since) : null;
  const until = permission.until ? parseDateOnlyString(permission.until) : null;

  const hasAnchorDays =
    permission?.anchor_days && permission?.anchor_days.length > 0;
  const receivedDays = permission?.anchor_days || [];
  const anchorDays = receivedDays.reduce((acc: AnchorDaysObject, day) => {
    acc[day as keyof AnchorDaysObject] = true;
    return acc;
  }, {} as AnchorDaysObject);

  return (
    <div className="flex flex-col items-center justify-between">
      <div className="flex items-center justify-start space-x-1 text-sm">
        {!since && !until ? (
          <span className="font-bold">Permanent</span>
        ) : (
          <>
            <span>
              {since ? format(since, templates.displayDateShort) + " " : null}
            </span>
            <span>—</span>
            {until ? (
              <div>{format(until, templates.displayDateShort)}</div>
            ) : (
              <span className="font-bold">No End</span>
            )}
          </>
        )}
      </div>
      {hasAnchorDays ? <AnchorDays anchorDays={anchorDays} /> : null}
    </div>
  );
}

const dayOrder: WeekDayNames[] = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday"
];

const AnchorDays = ({ anchorDays }: { anchorDays: AnchorDaysObject }) => {
  return (
    <div className="flex items-center space-x-1 text-neutral-500 text-xs">
      {dayOrder.map((day) => (
        <span
          key={day}
          className={cn(
            anchorDays[day as keyof AnchorDaysObject]
              ? "text-brand"
              : "text-neutral-500",
            "flex items-center justify-center font-semibold"
          )}
        >
          {day.charAt(0).toUpperCase()}
        </span>
      ))}
    </div>
  );
};
